import React, { useEffect, useState } from 'react';
import { Page, Text, View, Document, StyleSheet, PDFDownloadLink, pdf, Image, Font } from '@react-pdf/renderer';
import { formatDate } from '../../utility/formatDate';
import { Button } from 'reactstrap';
import { formatCamelCase, indianNumberWords } from './invoiceUtil';
import { TAXES } from '../../utility/constants';
import QRCode from 'qrcode';
import Hero from '../../components/Common/Hero';
import { getWarehouseListReq } from '../../service/branchService';
Font.register({
  family: 'Ubuntu',
  fonts: [
    { src: 'https://cdn.jsdelivr.net/fontsource/fonts/ubuntu@latest/latin-400-normal.ttf', fontWeight: 400 },
    { src: 'https://cdn.jsdelivr.net/fontsource/fonts/ubuntu@latest/latin-700-normal.ttf', fontWeight: 700 },
  ],
});

// Define styles for the PDF
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 8,
    fontFamily: 'Ubuntu',
    position: "relative",
  },

  pageBorder: {
    position: 'absolute',
    top: 30,
    right: 30,
    bottom: 30,
    left: 30,
    borderWidth: 1,
    borderColor: 'gray'
  },

  // HEADER
  headerRow: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#9e9e9e",
    minHeight: 110,
  },

  logoSection: {
    width: "18%",
    justifyContent: "center",
    alignItems: "center",
    padding: 5,
  },

  companySection: {
    width: "57%",
    padding: 8,
  },

  invoiceSection: {
    width: "25%",
    justifyContent: "flex-end",
    padding: 8,
  },

  logoImage: {
    width: 90,
    height: 60,
  },

  companyName: {
    fontSize: 9,
    fontFamily: 'Ubuntu',
    fontWeight: "bold",
    marginBottom: 4,
  },

  invoiceTitle: {
    fontSize: 22,
    textAlign: "right",
  },

  // INVOICE DETAILS
  infoRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#9e9e9e",
  },

  leftInfoBox: {
    width: "50%",
    padding: 8,
    borderRightWidth: 1,
    borderColor: "#9e9e9e",
  },

  rightInfoBox: {
    width: "50%",
    padding: 8,
  },

  infoLine: {
    flexDirection: "row",
    marginBottom: 4,
  },

  label: {
    width: 90,
    fontSize: 8,
  },

  value: {
    fontSize: 8,
    fontWeight: "bold",
  },

  // ADDRESS
  addressRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: "#9e9e9e",
  },
  addressBox: {
    width: "50%",
    minHeight: 100,
  },
  addressBoxLeft: {
    borderRightWidth: 1,
    borderColor: "#9e9e9e",
  },
  boxTitle: {
    fontSize: 9,
    fontWeight: "bold",
    backgroundColor: "#f2f3f4",
    borderBottomWidth: 1,
    borderBottomColor: "#9e9e9e",
    padding: 5,
    marginBottom: 5,
  },

  // TABLE
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f2f3f4",
    borderBottomWidth: 1,
    borderColor: "#9e9e9e",
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderColor: "#9e9e9e",
  },
  cell: {
    padding: 3,
    fontSize: 7.5,
    borderRightWidth: 1,
    borderColor: "#9e9e9e",
    textAlign: "center",
  },

  // Column widths
  colSr: {
    width: "4%",
  },

  colDesc: {
    width: "30%",
  },

  colUnit: {
    width: "8%",
  },

  colHsn: {
    width: "14%",
  },

  colQty: {
    width: "8%",
  },

  colRate: {
    width: "8%",
  },

  colTax: {
    width: "10%",
  },

  colAmount: {
    width: "10%",
  },

  // FOOTER
  footerRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#9e9e9e",
  },

  smallText: {
    fontSize: 8,
    marginBottom: 2,
  },

  bold: {
    fontWeight: "bold",
  },

  note: {
    fontSize: 8,
    marginTop: 40,
    textAlign: "right",
  },
  termsBox: {
    width: "65%",
    padding: 10,
    borderRightWidth: 1,
    borderColor: "#9e9e9e",
  },

  totalBox: {
    width: "35%",
  },

  totalTable: {
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#9e9e9e",
  },

  totalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 7,
    paddingVertical: 3,
  },

  totalLabel: {
    fontSize: 8,
  },

  totalValue: {
    fontSize: 8,
    textAlign: "right",
  },

  grandTotal: {
    fontWeight: "bold",
    fontSize: 9,
  },

  signatureBox: {
    padding: 10,
    minHeight: 80,
    justifyContent: "flex-end",
  },

  signatureText: {
    fontSize: 8,
    textAlign: "right",
  },

  greyLabel: {
    color: "#666",
    fontSize: 8,
    marginTop: 8,
    marginBottom: 2,
  },
  qrSection: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#9e9e9e",
    padding: 10,
    minHeight: 120,
  },

  qrImage: {
    width: 90,
    height: 90,
  },

  qrInfo: {
    justifyContent: 'center',
    marginLeft: 15,
    flex: 1,
    gap: 10
  },

  qrText: {
    fontSize: 8,
    marginBottom: 3,
  },
});


// Function to display table with pagination
const TableWithPagination = ({ data, taxes }) => {
  let rowData = [];
  let resp = data.responseObj?.line_items;
  resp.map((val) => {
    let obj = { ...val };
    val.line_item_taxes.map((item) => {
    if (item.tax_name.indexOf("SGST") != -1) {
      let s = item.tax_name.split(" ")[1];
      obj.sgst = s.substring(1, s.lastIndexOf(")"));
      obj.sgst_tax = item.tax_amount;
    } else if (item.tax_name.indexOf("CGST") != -1) {
      let s = item.tax_name.split(" ")[1];
      obj.cgst = s.substring(1, s.lastIndexOf(")"));
      obj.cgst_tax = item.tax_amount;
    }
    });
    rowData.push(obj);
  });
  return (
    <View>
      {/* Table Header */}
      <View style={styles.tableHeader} fixed>
        <Text style={[styles.cell, styles.colSr, styles.bold, { borderLeftWidth: 1, borderTopWidth: 1 ,textAlign:'right'}]}>#</Text>
        <Text style={[styles.cell, styles.colDesc, styles.bold, { borderTopWidth: 1, textAlign:'left'}]}> Description</Text>
        <Text style={[styles.cell, styles.colQty, styles.bold, { borderTopWidth: 1,textAlign:'right' }]}>Qty</Text>
        <Text style={[styles.cell, styles.colUnit, styles.bold, { borderTopWidth: 1,textAlign:'left' }]}>Unit</Text>
        <Text style={[styles.cell, styles.colHsn, styles.bold, { borderTopWidth: 1 ,textAlign:'right'}]}>HSN/SAC</Text>
        <Text style={[styles.cell, styles.colRate, styles.bold, { borderTopWidth: 1,textAlign:'right' }]}>Rate</Text>
        {taxes?.map((tax, index) => {
          return (
            <React.Fragment key={index}>
              <Text style={[styles.cell, styles.colTax, styles.bold, { borderTopWidth: 1,textAlign:'right' }]}>{tax.headerName}</Text>
              <Text style={[styles.cell, styles.colTax, styles.bold, { borderTopWidth: 1,textAlign:'right' }]}>Tax Amt</Text>
            </React.Fragment>
          );
        })}
        <Text style={[styles.cell, styles.colAmount, styles.bold, { borderTopWidth: 1,textAlign:'right' }]}>
          Amount
        </Text>
      </View>

      {/* Table Rows */}
      {rowData.map((item, index) => (
        <View style={styles.tableRow} key={index} wrap={false}>
          <Text style={[styles.cell, styles.colSr, { borderLeftWidth: 1 }]}>{index + 1}</Text>
          <Text style={[styles.cell, styles.colDesc,{textAlign:'left'}]}>{item.description}</Text>
          <Text style={[styles.cell, styles.colQty,{textAlign:'right'}]}>{item.quantity}</Text>
          <Text style={[styles.cell, styles.colUnit,{textAlign:'left'}]}>{item.unit}</Text>
          <Text style={[styles.cell, styles.colHsn,{textAlign:'right'}]}>{item.hsn_or_sac}</Text>
          <Text style={[styles.cell, styles.colRate,{textAlign:'right'}]}>{item.rate}</Text>
          {
            taxes.map((tax) => {
              const matchingTax = item.line_item_taxes.find((lineItemTax) =>
                lineItemTax.tax_name.startsWith(tax.headerName) // Check if the tax_name contains the tax header
              );

              if (matchingTax) {
                const percentage = matchingTax.tax_name.match(/\((\d+)%\)/); // Extract percentage using regex
                const percentageText = percentage ? percentage[1] : ''; // Extracted percentage or empty if not found

                return (
                  <>
                    <Text style={[styles.cell, styles.colTax,{textAlign:'right'}]}>{percentageText + "%"}</Text>
                    <Text style={[styles.cell, styles.colAmount,{textAlign:'right'}]}>
                      {matchingTax.tax_amount}
                    </Text>
                  </>
                );
              }

              return (
                <>
                  <Text style={[styles.cell, styles.colTax]}>
                    -
                  </Text>
                  <Text style={[styles.cell, styles.colTax]}>
                    -
                  </Text>
                </>
              )
            })
          }
          <Text style={[styles.cell, styles.colAmount,{textAlign:'right'}]}>
            {item.item_total}
          </Text>
        </View>

      ))}
    </View>
  );
};

// Define the PDF document structure
const PdfDocument = ({ data, qrImage,selectedWarehouse }) => {
  const amountText = formatCamelCase(indianNumberWords(Math.floor(data.responseObj.total)));
  let cgst = "",
    sgst = "";
  data.responseObj.taxes.map((val) => {
    if (val.tax_name.indexOf("CGST") !== -1) {
      cgst = val.tax_amount;
    } else if (val.tax_name.indexOf("SGST") !== -1) {
      sgst = val.tax_amount;
    }
  });

  //const [taxes, setTaxes] = useState([]);

  const taxes = React.useMemo(() => {
    const uniqueTaxes = new Set();
    data.responseObj.taxes.forEach((tax) => {
      for (const taxType in TAXES) {
        if (tax.tax_name.startsWith(TAXES[taxType])) {
          uniqueTaxes.add(TAXES[taxType]);
          break;
        }
      }
    });


    return [...uniqueTaxes].map(taxName => ({ headerName: taxName }));
  }, [data.responseObj.taxes]);


  return (
    <Document>
      <Page size="A4" style={styles.page} wrap>
        <View fixed style={styles.pageBorder} />
        <View style={styles.headerRow}>
          <View style={[styles.logoSection, { width: '25% !important' }]}>
            <Image src={require("../../assets/images/Willsmeet-Logo.png")} style={styles.logoImage} />
          </View>
          <View style={[styles.companySection, { width: '40% !important' }]}>
          <Text style={[styles.smallText,styles.bold,{ paddingLeft: 8 }]}>Bansi Office Solutions Private Limited</Text>
          <Text style={[styles.smallText,{ paddingLeft: 8 }]}>{selectedWarehouse?.address?.addressLine1||"No. 1496, Opp. Park Square Apartment,"}</Text>
          <Text style={[styles.smallText,{ paddingLeft: 8 }]}>{selectedWarehouse?.address?.addressLine2||"19th Main, 1st Sector, HSR Layout"}</Text>
          <Text style={[styles.smallText,{ paddingLeft: 8 }]}>{selectedWarehouse?.address?.city||"Bengaluru"} -{selectedWarehouse?.address?.pincode||"560102"}, {selectedWarehouse?.address?.state||"Karnataka"}</Text>
          <Text style={[styles.smallText,{ paddingLeft: 8 }]}>GST IN: {selectedWarehouse?.gstIn}</Text>
          <Text style={[styles.smallText,{ paddingLeft: 8 }]}>Web: www.willsmeet.com</Text>
          <Text style={[styles.smallText,{ paddingLeft: 8 }]}>Email: {selectedWarehouse?.email||"sales@willsmeet.com"}</Text>
          </View>
          <View style={[styles.invoiceSection, { width: '35% !important' }]}>
            <Text style={styles.invoiceTitle}>TAX INVOICE</Text>
          </View>
        </View>

        <View style={styles.infoRow}>
          <View style={styles.leftInfoBox}>
            <View style={styles.smallText}>
              <Text><Text style={{fontWeight:'600'}}>Invoice Number: </Text>{data.responseObj.invoice_number}</Text>
            </View>
            <View style={styles.smallText}>
              <Text><Text style={{fontWeight:'600'}}>Invoice Date: </Text>{formatDate(data.responseObj.date)}</Text>
            </View>
          </View>
          <View style={styles.rightInfoBox}>
            <View style={styles.smallText}>
              <Text><Text style={{fontWeight:'600'}}>Order ID: </Text>{data.responseObj.salesorder_number}</Text>
            </View>
            <View style={styles.smallText}>
              <Text><Text style={{fontWeight:'600'}}>Place of Supply: </Text>{data.responseObj.place_of_supply}</Text>
            </View>
          </View>
        </View>
        <View style={styles.addressRow}>
          <View style={[styles.addressBox, styles.addressBoxLeft]}>
            <Text style={[styles.boxTitle, { padding: 5 }]}>Billing Address:</Text>
            <Text style={[styles.smallText,styles.bold,{ paddingLeft: 8 }]}>{data.responseObj?.customer_name}</Text>
            <Text style={[styles.smallText,{ paddingLeft: 8 }]}>{data.responseObj.billing_address?.attention}</Text>
            <Text style={[styles.smallText, { paddingLeft: 8 }]}>{data.responseObj.billing_address?.street}</Text>
            <Text style={[styles.smallText, { paddingLeft: 8 }]}>{data.responseObj.billing_address?.street2}</Text>
            <Text style={[styles.smallText, { paddingLeft: 8 }]}>{data.responseObj.billing_address?.city}</Text>
            <Text style={[styles.smallText, { paddingLeft: 8 }]}>{data.responseObj.billing_address?.zip} {data.responseObj.billing_address?.state} {data.responseObj.billing_address?.country}</Text>
          </View>
          <View style={styles.addressBox}>
            <Text style={[styles.boxTitle, { padding: 5 }]}>Shipping Address:</Text>
            <Text style={[styles.smallText,{ paddingLeft: 8 }]}>{data.responseObj.shipping_address?.attention}</Text>
            <Text style={[styles.smallText, { paddingLeft: 8 }]}>{data.responseObj.shipping_address?.street}</Text>
            <Text style={[styles.smallText, { paddingLeft: 8 }]}>{data.responseObj.shipping_address?.street2}</Text>
            <Text style={[styles.smallText, { paddingLeft: 8 }]}>{data.responseObj.billing_address?.city}</Text>
            <Text style={[styles.smallText, { paddingLeft: 8 }]}>{data.responseObj.billing_address?.zip} {data.responseObj.billing_address?.state} {data.responseObj.billing_address?.country}</Text>
          </View>
        </View>

        <View>
          <TableWithPagination data={data} taxes={taxes} />
          <View style={styles.footerRow}>
            {/* Left Side */}
            <View style={[styles.termsBox, { borderBottomWidth: 0 }]}>
              <Text style={styles.greyLabel}>Total in words</Text>
              <Text style={styles.smallText}>
                {"Indian Rupee " +
                  amountText.charAt(0).toUpperCase() +
                  amountText.slice(1)}
              </Text>
              <Text style={styles.greyLabel}>Notes</Text>
              <Text style={styles.smallText}>
                {data.responseObj.notes}
              </Text>

              <Text style={styles.greyLabel}>Terms & Conditions</Text>

              {data.responseObj.terms
                ?.split(/\r?\n/)
                .map((term, index) => (
                  <Text key={index} style={styles.smallText}>
                    {term}
                  </Text>
                ))}
            </View>

            {/* Right Side */}
            <View style={styles.totalBox}>
              <View style={styles.totalTable}>
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>Sub Total</Text>
                  <Text style={styles.totalValue}>
                    {parseFloat(data.responseObj.sub_total).toFixed(2)}
                  </Text>
                </View>

                {data.responseObj.taxes.map((tax, index) => (
                  <View key={index} style={styles.totalRow}>
                    <Text style={styles.totalLabel}>
                      {tax.tax_name}
                    </Text>
                    <Text style={styles.totalValue}>
                      {parseFloat(tax.tax_amount).toFixed(2)}
                    </Text>
                  </View>
                ))}

                <View style={styles.totalRow}>
                  <Text style={styles.grandTotal}>Total</Text>
                  <Text style={styles.grandTotal}>
                    {parseFloat(data.responseObj.total).toFixed(2)}
                  </Text>
                </View>
              </View>

              <View style={styles.signatureBox}>
                <Text style={styles.signatureText}>
                  For Bansi Office Solutions Private Limited
                </Text>

                <Text
                  style={[
                    styles.signatureText,
                    { marginTop: 35 }
                  ]}
                >
                  Authorised Signatory
                </Text>
              </View>
            </View>
          </View>
        </View>
        {qrImage && (
          <View style={styles.qrSection}>
            <Image
              src={qrImage}
              style={styles.qrImage}
            />

            <View style={styles.qrInfo}>
             

             <Text style={styles.qrText}>
                IRN:{"          "}<Text style={{fontWeight:'bold'}}>
                {data.responseObj.einvoice_details?.inv_ref_num}
              </Text>
              </Text>

              <Text style={styles.qrText}>
                ACK No:{"   "}
              <Text style={{fontWeight:'bold'}}>
                {data.responseObj.einvoice_details?.ack_number}
              </Text>
               </Text>
            </View>
          </View>
        )}
        <Text
          fixed
          style={{
            position: 'absolute',
            bottom: 15,
            left: 0,
            right: 30,
            fontSize: 8,
            textAlign: 'right',
          }}
          render={({ pageNumber, totalPages }) =>
            totalPages > 1 ? `${pageNumber}` : ''
          }
        />
      </Page>
    </Document>
  )
};
const generateQRCode = async (text) => {
  try {
    return await QRCode.toDataURL(text);
  } catch (err) {
    console.error(err);
    return null;
  }
};

const InvoicePdfComponent = ({ data }) => {
  const [warehouse,setWarehouse]=useState([])
  const [selectedWarehouse,setSelectedWarehouse]=useState(null)
  const handleOpenPdf = async () => {
    const qrImage = await generateQRCode(
      data.responseObj.einvoice_details?.qr_link
    );
    const blob = await pdf(
      <PdfDocument
        data={data}
        qrImage={qrImage}
        selectedWarehouse={selectedWarehouse}
      />
    ).toBlob();

    const url = URL.createObjectURL(blob);

    const fileName =
      `${data?.responseObj?.salesorder_number || "document"}.pdf`;

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;

    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
  };
  const getWarehouses=async()=>{
   try{
      const response=await getWarehouseListReq()
      setWarehouse(response?.payload?.warehouses)
   }
   catch(error)
   {
    console.log(error)
   }
  }
  useEffect(()=>{
    getWarehouses()
  },[])
  useEffect(()=>{
    if(!data?.responseObj||!warehouse?.length)return
    const matchedWarehouses=warehouse.find(
      (warehouse)=>
        warehouse?.zohoLocationId===data?.responseObj?.location_id
      
    )
    setSelectedWarehouse(matchedWarehouses)
  },[warehouse,data?.responseObj])

  return (
    <div>
      {/* <PDFDownloadLink document={<PdfDocument data={data} />} fileName={`${data.purchaseOrderNumber}.pdf`}>
        {({ blob, url, loading, error }) =>
          <Button color="primary" outline onClick={handleOpenPdf}>{loading ? 'Loading...' : 'Download PDF'}</Button>
        }
      </PDFDownloadLink> */}
      <Button color="primary" outline onClick={handleOpenPdf}>Download PDF</Button>
    </div>
  );
};

export default InvoicePdfComponent;